import { lastTouchInput } from "@/shared/utils/attribution";
import { streamAssistantMessage } from "../api/streamAssistantMessage";
import {
  applyEvent,
  beginTurn,
  finishTurn,
  getAssistantState,
  startNewChat,
} from "./assistantStore";

export interface AssistantTurnInput {
  text: string;
  locale: string;
  pageUrl: string;
  contentId: string | null;
  productId?: string | null;
  origin?: "BUBBLE" | "ERROR";
  errorCode?: string | null;
  paymentId?: string | null;
  playerError?: string | null;
}

// Текущий ход. События оборванного хода (новый чат, выход) в ленту не
// попадают: сравниваем контроллер, с которым ход стартовал.
let currentTurn: AbortController | null = null;

/**
 * Отправляет реплику зрителя и наполняет ленту событиями потока. Закрытие
 * панели ход не обрывает: бэкенд всё равно допишет и сохранит ответ.
 */
export async function sendAssistantTurn(
  input: AssistantTurnInput,
  deps: { stream: typeof streamAssistantMessage } = {
    stream: streamAssistantMessage,
  },
): Promise<"ok" | "busy" | "unauthorized"> {
  if (getAssistantState().sending) return "busy";
  const origin = input.origin ?? "BUBBLE";
  // Ход из экрана ошибки всегда начинает новый диалог (бэкенд делает так же).
  const conversationId =
    origin === "ERROR" ? null : getAssistantState().conversationId;
  beginTurn(input.text, origin);
  const turn = new AbortController();
  currentTurn = turn;
  try {
    await deps.stream(
      {
        conversationId,
        text: input.text,
        locale: input.locale,
        pageUrl: input.pageUrl,
        contentId: input.contentId,
        productId: input.productId ?? null,
        origin,
        errorCode: input.errorCode ?? null,
        paymentId: input.paymentId ?? null,
        playerError: input.playerError ?? null,
        utm: lastTouchInput(),
      },
      (event) => {
        if (currentTurn === turn) applyEvent(event);
      },
      turn.signal,
    );
    if (currentTurn === turn) finishTurn();
    return "ok";
  } catch (e) {
    if (currentTurn !== turn) return "ok";
    finishTurn({ failed: true });
    return e instanceof Error && e.message === "UNAUTHORIZED"
      ? "unauthorized"
      : "ok";
  } finally {
    if (currentTurn === turn) currentTurn = null;
  }
}

/** Обрывает текущий ход, если он идёт. */
export function abortAssistantTurn() {
  const turn = currentTurn;
  currentTurn = null;
  turn?.abort();
}

/** «Новый диалог»: обрыв хода и чистая лента. */
export function resetAssistantChat() {
  abortAssistantTurn();
  startNewChat();
}
